import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import Translate from 'ui/atoms/translate';
import Currency from 'ui/atoms/currency';
import BoxedContent from 'ui/molecules/boxed-content';
import { camelCase } from 'change-case';
import { AdminProductDetail, AdminProductOverview, Issuer, ProductValidationStatusEnum } from 'api';
import * as Styled from './styled';
import Icon from 'src/ui/atoms/icon';
import CardItem from 'src/ui/atoms/card-item';
import CopyButton from 'src/ui/molecules/copy-button';
import ActionButtons from 'src/ui/molecules/action-buttons';
import Button from 'src/ui/atoms/button';
import EditProduct from '../../products/edit-product';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import Number from 'src/ui/atoms/number';
import DocumentModal from 'src/libraries/document-modal';
import StatusTag from 'ui/atoms/status-tag';
import { getProductStatusVariant } from 'ui/helper/variant';
import { getNetworkType, getWalletAddressForNetworkAssertExistence } from 'ui/helper/networks';
import { currentTokenHasManageDataPermission } from 'src/apps/issuer/helpers/tokenPermissions';

export interface ProductOverviewProps {
  data: Omit<AdminProductOverview, 'network'> & AdminProductDetail;
  issuer: Issuer;
  refreshProductData: () => void;
  hasEditIssuanceDocumentsPermission: boolean;
}

const ProductOverview: FunctionComponent<ProductOverviewProps> = (props) => {
  const { data, issuer, refreshProductData, hasEditIssuanceDocumentsPermission } = props;

  const [isEditProductModalOpen, setEditProductModalOpen] = useState(false);

  const translateWithStringArgs = useTranslateWithStringArgs();

  const productData = useMemo(() => {
    return {
      paperContractHash: data.paperContractHash,
      ...(data.ewpg === true
        ? {
            publicationRestricted: data.publicationRestricted
              ? translateWithStringArgs('commonOptions.true')
              : translateWithStringArgs('commonOptions.false'),
          }
        : {}),
      isin: data.isin,
      code: data.code?.toString(),
      calculationFactor: data.calculationFactor?.toString(),
      backupWallet: data.backupWallet,
      lockInTime: data.lockInTime ? data.lockInTime?.toString() : null,
      currentMarketPrice: data.latestProductPrice && <Currency>{data.latestProductPrice}</Currency>,
      provideNominalValue: <Translate name={`commonOptions.${data.provideNominalValue ? 'true' : 'false'}`} />,
      nominalValue: data.nominalValue && <Currency>{data.nominalValue}</Currency>,
      issuancePrice: data.issuancePrice && <Currency>{data.issuancePrice}</Currency>,
      agioPercent: data.agioPercent && (
        <>
          <Number minimumFractionDigits={2}>{data.agioPercent}</Number>%
        </>
      ),
      stepSize: data.stepSize,
      maxUnitsNumber: data.maxIssuableAmount?.toString(),
      paymentDeadline: data.paymentDeadline?.toString(),
      cancellationDeadline: data.cancellationDeadline && data.cancellationDeadline.toString(),
      issuanceDocument: (
        <Translate
          name="common.viewDocument"
          args={[
            (text: string, key) => {
              return (
                <DocumentModal
                  key={key}
                  {...data.issuanceDocument?.document}
                  title={<Translate name="dashboardIssuerProductDetails.issuanceDocument" />}
                >
                  {text}
                </DocumentModal>
              );
            },
          ]}
        />
      ),
      issuanceDocumentVersion: data.issuanceDocument?.version,
    };
  }, [JSON.stringify(data)]);

  const onEditProduct = useCallback(() => {
    refreshProductData();
    setEditProductModalOpen(false);
  }, []);

  const canEditProduct = useMemo(() => currentTokenHasManageDataPermission(data), [JSON.stringify(data)]);

  const isProductValidated = useMemo(
    () =>
      data.ewpg === true &&
      data.validationStatus === ProductValidationStatusEnum.VALIDATION_SUCCESS &&
      !!data.publicationValid,
    [data.ewpg, data.validationStatus, data.publicationValid],
  );

  const isProductUnderValidation = useMemo(
    () => data.ewpg === true && data.validationStatus === ProductValidationStatusEnum.VALIDATION_PENDING,
    [data.ewpg, data.validationStatus],
  );

  return (
    <BoxedContent title={<Translate name="common.generalInformation" />}>
      <CardItem
        title={<Translate name="dashboardIssuerProductDetails.issuer" />}
        content={data.companyName}
        icon={
          <Styled.IconLink href={`/dashboard/issuers/${issuer.id}`}>
            <Icon name="link" />
          </Styled.IconLink>
        }
      />
      <CardItem
        title={<Translate name="dashboardIssuerProductDetails.issuerWalletAddress" />}
        content={
          data?.network && data.issuerWallet
            ? getWalletAddressForNetworkAssertExistence(getNetworkType(data?.network), data.issuerWallet)
            : '-'
        }
      />
      <CardItem title={<Translate name="dashboardIssuerProductDetails.organization" />} content={data?.organization} />
      <CardItem
        title={<Translate name="dashboardIssuerProductDetails.productType" />}
        content={<Translate name={`productType.${camelCase(data.productType)}`} />}
      />
      <CardItem
        title={<Translate name="dashboardIssuerProductDetails.totalVolume" />}
        content={<Number>{data.totalVolume}</Number>}
      />
      <CardItem
        title={<Translate name="dashboardIssuerProductDetails.status" />}
        content={
          <StatusTag variant={getProductStatusVariant(data.status)} truncate={true}>
            <Translate name={`issuerProducts.status.${data.status}`} />
          </StatusTag>
        }
      />
      <CardItem
        title={<Translate name="dashboardIssuerProductDetails.network" />}
        content={<Styled.Network>{data.network}</Styled.Network>}
      />
      <CardItem
        title={<Translate name="entryType.label" />}
        content={<Translate name={`entryType.${data.entryType}`} />}
      />
      <CardItem
        title={<Translate name="dashboardIssuerProductDetails.ewpg" />}
        content={data.ewpg ? <Translate name={`commonOptions.true`} /> : <Translate name={`commonOptions.false`} />}
      />
      <CardItem
        title={<Translate name={`dashboardIssuerProductDetails.smartContract`} />}
        content={data.smartContractAddress}
        icon={<CopyButton valueToCopy={data.smartContractAddress} />}
      />
      {Object.entries(productData).map((value: any, key: number) => {
        return <CardItem title={<Translate name={`dashboardIssuerProductDetails.${value[0]}`} />} content={value[1]} />;
      })}

      {(canEditProduct || (hasEditIssuanceDocumentsPermission && isProductValidated)) && (
        <ActionButtons spacingTop="regular">
          <Button variant="secondary" onClick={() => setEditProductModalOpen(true)}>
            <Translate name="common.edit" />
          </Button>
        </ActionButtons>
      )}
      {isEditProductModalOpen && (
        <EditProduct
          onClose={() => setEditProductModalOpen(false)}
          data={data}
          onEditProductSuccess={onEditProduct}
          canEditProduct={canEditProduct}
          canEditIssuanceDocuments={hasEditIssuanceDocumentsPermission}
          isProductValidated={isProductValidated}
          isProductUnderValidation={isProductUnderValidation}
        />
      )}
    </BoxedContent>
  );
};

export default ProductOverview;
